import React from 'react';
import { Modal, Button, Divider } from 'src/components/design-system';
import { Spin } from 'antd';
import { Table, textWithFallback } from 'src/components/design-system/table';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getContacts } from 'shared/store/selectors/contact.selector';
import { DateTime } from 'luxon';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { formatFullName, formatNumber } from 'shared/utils';
import Logger from 'shared/utils/Logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import styles from './views-modal.module.scss';

// Map period to days
const PERIOD_TO_DAYS = {
  last7Days: 7,
  last30Days: 30,
  last60Days: 60,
  last90Days: 90,
  total: null,
};

/**
 * Views Modal Component
 *
 * Displays detailed analytics information for a knowledge item with user activity
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - The knowledge item with analytics data
 * @param {string} props.period - The period to show ('last90Days' or 'total')
 * @param {Function} props.onClose - Callback to close the modal
 */
export const ViewsModal = ({ open = false, item, period, onClose }) => {
  item = item ?? {};
  const lang = useSelector(getLang('MY_CONTENT'));
  const users = useSelector(getContacts);
  const { getValueFromLang } = useProfileLang();
  const [analyticsData, setAnalyticsData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [gridApi, setGridApi] = React.useState(null);

  const usersById = React.useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
  }, [users]);

  const analytics = React.useMemo(() => item.analytics || {}, [item.analytics]);

  React.useEffect(() => {
    if (!open || !item.id) return;

    let cancelled = false;
    const fetchAnalytics = async () => {
      setAnalyticsData([]);
      setLoading(true);
      try {
        const params = { maxResults: 1000 };
        const days = PERIOD_TO_DAYS[period];
        if (days) {
          const endDate = DateTime.now();
          params.startDate = endDate.minus({ days }).toISODate();
          params.endDate = endDate.toISODate();
        }

        const response = await knowledgeServiceApi.getKnowledgeItemAnalytics({
          id: item.id,
          params,
        });

        if (cancelled) return;

        if (response.error) {
          Logger.error('Failed to fetch analytics:', { error: response.error });
          setAnalyticsData([]);
          return;
        }

        const { rows = [] } = response.data || {};
        const rowsWithUsers = rows
          .map(row => {
            const user = usersById[row.user_id];
            if (!user) return null;
            return { ...row, user };
          })
          .filter(Boolean);

        setAnalyticsData(rowsWithUsers);
      } catch (error) {
        if (!cancelled) Logger.error('Error fetching analytics data:', { error });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      cancelled = true;
    };
  }, [open, item.id, period, usersById]);

  const columns = React.useMemo(
    () => [
      {
        headerName: lang.NAME,
        colId: 'name',
        sortable: true,
        valueGetter: params => (params.data?.user ? formatFullName(params.data.user) : null),
        valueFormatter: textWithFallback,
        comparator: (valueA, valueB) => {
          const nameA = (valueA || '').toLowerCase();
          const nameB = (valueB || '').toLowerCase();
          return nameA.localeCompare(nameB);
        },
      },
      {
        headerName: lang.PROFESSION,
        colId: 'profession',
        sortable: true,
        valueGetter: params => getValueFromLang(params.data?.user?.profession),
        comparator: (valueA, valueB) => {
          const profA = (valueA || '').toLowerCase();
          const profB = (valueB || '').toLowerCase();
          return profA.localeCompare(profB);
        },
      },
      {
        headerName: lang.ROLE,
        colId: 'role',
        sortable: true,
        valueGetter: params => getValueFromLang(params.data?.user?.professional?.role),
        comparator: (valueA, valueB) => {
          const roleA = (valueA || '').toLowerCase();
          const roleB = (valueB || '').toLowerCase();
          return roleA.localeCompare(roleB);
        },
      },
      {
        headerName: lang.DATE_VIEWED,
        field: 'time_day.value',
        colId: 'time_day',
        sortable: true,
        cellRenderer: params =>
          params.value ? DateTime.fromISO(params.value).toLocaleString(DateTime.DATE_MED) : '-',
      },
      {
        headerName: lang.DAILY_VIEWS,
        field: 'views',
        colId: 'views',
        sortable: true,
        cellRenderer: params => params.value || 0,
      },
    ],
    [lang, getValueFromLang],
  );

  // Format last viewed text
  const lastViewedText = React.useMemo(() => {
    const uniqueViews = analytics.uniqueViews?.[period] || 0;
    const lastUpdated = analytics.lastViewedAt
      ? DateTime.fromISO(analytics.lastViewedAt).toLocaleString(DateTime.DATETIME_SHORT)
      : DateTime.now().toLocaleString(DateTime.DATETIME_SHORT);

    return lang.ANALYTICS_SUMMARY.replace('{{uniqueViews}}', uniqueViews).replace(
      '{{lastUpdated}}',
      lastUpdated,
    );
  }, [analytics, period, lang]);

  const handleDownloadCSV = React.useCallback(() => {
    // Prepare CSV headers using the same as table columns
    const headers = [lang.NAME, lang.PROFESSION, lang.ROLE, lang.DATE_VIEWED, lang.DAILY_VIEWS];

    // Get displayed rows from AgGrid (respects current filter and sort)
    const displayedRows = [];
    gridApi?.forEachNodeAfterFilterAndSort(node => displayedRows.push(node.data));

    // Prepare CSV rows from displayed data
    const rows = displayedRows.map(record => [
      record.user ? formatFullName(record.user) : '-',
      getValueFromLang(record.user?.profession),
      getValueFromLang(record.user?.professional?.role),
      record.time_day?.value
        ? DateTime.fromISO(record.time_day.value).toLocaleString(DateTime.DATE_MED)
        : '-',
      record.views || 0,
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const currentDate = DateTime.now().toISODate(); // YYYY-MM-DD format
    link.setAttribute('download', `Views Report ${item.title} ${currentDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [gridApi, item.title, lang, getValueFromLang]);

  // Format modal title
  const modalTitle = React.useMemo(() => {
    const views = analytics.views?.[period] || 0;
    const periodMap = {
      last7Days: lang.PERIOD_LAST_7D,
      last30Days: lang.PERIOD_LAST_30D,
      last60Days: lang.PERIOD_LAST_60D,
      last90Days: lang.PERIOD_LAST_90D,
      total: lang.PERIOD_ALL_TIME,
    };
    const periodLabel = periodMap[period] || '';
    const formattedViews = formatNumber(views);

    return lang.ANALYTICS_TITLE.replace('{{views}}', formattedViews).replace(
      '{{period}}',
      periodLabel,
    );
  }, [analytics, period, lang]);

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        <div className={styles.modalFooter}>
          <Button disabled={!analyticsData.length} onClick={handleDownloadCSV} type="default">
            <FontAwesomeIcon icon={faDownload} className={styles.downloadIcon} />
            {lang.DOWNLOAD_CSV}
          </Button>
          <Button onClick={onClose} type="primary">
            {lang.CLOSE}
          </Button>
        </div>
      }>
      <p className={styles.lastViewedText}>{lastViewedText}</p>

      <Divider />

      <Table
        containerProps={{ className: styles.tableContainer }}
        columnDefs={columns}
        rowData={analyticsData}
        getRowId={params => `${params.data.user_id}-${params.data.time_day?.value || ''}`}
        onGridReady={params => setGridApi(params.api)}
      />
      {loading && (
        <div className={styles.loadingSpinner}>
          <Spin />
        </div>
      )}
    </Modal>
  );
};
